// Stage 73.20cq (Phase C) — Selective Payroll Re-run modal / page.
//
// Two-step wizard:
//   1) Preview: shows the impacted-employees list with reason tags,
//      checkboxes (select all / individual), and per-employee gross/net.
//   2) Progress: kicks off /rerun-selective, polls /rerun-progress
//      every 2s, and finally renders the /rerun-diff old-vs-new table.
//
// Route: /payroll/rerun/:runId

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  RotateCcw, CheckSquare, Square, ChevronLeft, PlayCircle,
  AlertTriangle, CheckCircle2, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SelectivePayrollRerun() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('preview');   // preview | running | done
  const [impacted, setImpacted] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [diff, setDiff] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchImpacted = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/payroll/runs/${runId}/impacted-employees`, { withCredentials: true });
      setImpacted(r.data.employees || []);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Failed to load impacted employees.');
    }
    setLoading(false);
  }, [runId]);

  useEffect(() => { fetchImpacted(); }, [fetchImpacted]);

  const toggleSelect = (eid) => {
    const next = new Set(selectedIds);
    if (next.has(eid)) next.delete(eid); else next.add(eid);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (selectedIds.size === impacted.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(impacted.map((e) => e.employee_id)));
  };

  const startRerun = async () => {
    if (selectedIds.size === 0) return;
    setStep('running');
    setErrorMsg('');
    try {
      const res = await axios.post(
        `${API_URL}/payroll/runs/${runId}/rerun-selective`,
        { employee_ids: Array.from(selectedIds), reason },
        { withCredentials: true },
      );
      setJob(res.data);
      pollProgress();
    } catch (e) {
      setStep('done');
      setErrorMsg(e.response?.data?.detail || 'Selective re-run failed to start.');
    }
  };

  const pollProgress = async () => {
    try {
      const r = await axios.get(`${API_URL}/payroll/runs/${runId}/rerun-progress`, { withCredentials: true });
      setJob(r.data);
      if (['completed', 'failed', 'partially_completed'].includes(r.data.status)) {
        // Fetch diff
        const ids = Array.from(selectedIds).join(',');
        const d = await axios.get(`${API_URL}/payroll/runs/${runId}/rerun-diff?employee_ids=${ids}`, { withCredentials: true });
        setDiff(d.data.diffs || []);
        setStep('done');
      } else {
        setTimeout(pollProgress, 2000);
      }
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Progress polling failed.');
      setStep('done');
    }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const statusBadge = (s) => {
    const map = {
      running: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      partially_completed: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700',
      not_started: 'bg-slate-100 text-slate-500',
    };
    return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || 'bg-slate-100 text-slate-600'}`;
  };

  return (
    <div data-testid="selective-rerun-page" className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center space-x-2">
              <RotateCcw className="w-6 h-6 text-[#0052FF]" />
              <span>Selective Payroll Re-run</span>
            </h1>
            <p className="text-sm text-slate-500">Run ID: {runId}</p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div data-testid="rerun-error" className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 flex items-start space-x-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /><p className="text-sm">{errorMsg}</p>
        </div>
      )}

      {step === 'preview' && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Impacted Employees</h3>
                <p className="text-xs text-slate-500">
                  Only employees whose inputs have changed since last processing are shown.
                  Select the ones you want to re-run — approved payslips for the rest stay untouched.
                </p>
              </div>
              <button data-testid="toggle-all-btn" onClick={toggleAll} className="text-xs text-[#0052FF] hover:underline">
                {selectedIds.size === impacted.length ? 'Clear all' : 'Select all'}
              </button>
            </div>

            {loading && <div className="flex items-center space-x-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /><span>Loading impacted employees…</span></div>}

            {!loading && impacted.length === 0 && (
              <div className="text-center py-10">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">No impacted employees.</p>
                <p className="text-xs text-slate-500">All inputs are up-to-date with the last payroll processing.</p>
              </div>
            )}

            <div className="divide-y divide-slate-100">
              {impacted.map((e) => (
                <div key={e.employee_id} data-testid={`impacted-row-${e.employee_id}`} className="flex items-start space-x-3 py-3 hover:bg-slate-50 rounded px-2">
                  <button data-testid={`select-${e.employee_id}`} onClick={() => toggleSelect(e.employee_id)} className="mt-1">
                    {selectedIds.has(e.employee_id) ? <CheckSquare className="w-5 h-5 text-[#0052FF]" /> : <Square className="w-5 h-5 text-slate-400" />}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-slate-800">{e.employee_name || '—'}</span>
                        {e.employee_code && <span className="ml-2 text-xs text-slate-500">({e.employee_code})</span>}
                      </div>
                      <div className="text-xs text-slate-500">Gross: {inr(e.gross_pay)} · Net: {inr(e.net_pay)}</div>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {e.reasons.map((r, i) => (
                        <span key={i} title={r.message} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          {r.code}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {impacted.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <label className="block text-xs text-slate-500">Reason (optional)</label>
              <input data-testid="rerun-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
                     className="w-full border rounded px-3 py-1.5 text-sm" placeholder="e.g. LOP correction for Feb, arrear disbursement" />
              <button data-testid="start-rerun-btn" onClick={startRerun}
                      disabled={selectedIds.size === 0}
                      className="bg-[#0052FF] hover:bg-[#0040CC] disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2">
                <PlayCircle className="w-4 h-4" />
                <span>Re-run {selectedIds.size} Employee{selectedIds.size !== 1 ? 's' : ''}</span>
              </button>
            </div>
          )}
        </>
      )}

      {step === 'running' && (
        <div data-testid="rerun-running" className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-3">
          <Loader2 className="w-10 h-10 text-[#0052FF] mx-auto animate-spin" />
          <p className="text-sm font-medium text-slate-800">Re-running payroll for {job?.total || selectedIds.size} employees…</p>
          <div className="max-w-md mx-auto">
            <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className="bg-[#0052FF] h-2 transition-all" style={{ width: `${job?.total ? Math.round((job.processed || 0) / job.total * 100) : 20}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{job?.processed || 0} processed</span>
              <span>{job?.failed || 0} failed</span>
              <span>{job?.pending ?? job?.total} pending</span>
            </div>
          </div>
        </div>
      )}

      {step === 'done' && job && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800">Re-run Summary</h3>
              <span data-testid="job-status-badge" className={statusBadge(job.status)}>{(job.status || '').replace('_', ' ')}</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="border rounded-md p-3"><p className="text-xs text-slate-500">Total</p><p className="text-lg font-bold text-slate-800">{job.total}</p></div>
              <div className="border rounded-md p-3"><p className="text-xs text-slate-500">Processed</p><p className="text-lg font-bold text-emerald-700">{job.processed}</p></div>
              <div className="border rounded-md p-3"><p className="text-xs text-slate-500">Failed</p><p className="text-lg font-bold text-red-700">{job.failed}</p></div>
              <div className="border rounded-md p-3"><p className="text-xs text-slate-500">Pending</p><p className="text-lg font-bold text-slate-800">{job.pending}</p></div>
            </div>
          </div>

          {diff.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Old vs New</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr className="text-left">
                      <th className="py-2">Employee</th>
                      <th className="text-right">Old Gross</th>
                      <th className="text-right">New Gross</th>
                      <th className="text-right">Δ Gross</th>
                      <th className="text-right">Old Net</th>
                      <th className="text-right">New Net</th>
                      <th className="text-right">Δ Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {diff.map((d) => (
                      <tr key={d.employee_id} data-testid={`diff-row-${d.employee_id}`}>
                        <td className="py-2">{d.employee_id.slice(-6)}</td>
                        <td className="text-right">{inr(d.old.gross_pay)}</td>
                        <td className="text-right">{inr(d.new.gross_pay)}</td>
                        <td className={`text-right font-medium ${d.delta.gross_pay > 0 ? 'text-emerald-700' : d.delta.gross_pay < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                          {d.delta.gross_pay > 0 ? '+' : ''}{inr(d.delta.gross_pay)}
                        </td>
                        <td className="text-right">{inr(d.old.net_pay)}</td>
                        <td className="text-right">{inr(d.new.net_pay)}</td>
                        <td className={`text-right font-medium ${d.delta.net_pay > 0 ? 'text-emerald-700' : d.delta.net_pay < 0 ? 'text-red-700' : 'text-slate-500'}`}>
                          {d.delta.net_pay > 0 ? '+' : ''}{inr(d.delta.net_pay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <button data-testid="back-to-run-btn" onClick={() => navigate(`/payroll/runs`)}
                    className="bg-slate-800 text-white px-4 py-2 rounded text-sm">
              Back to Payroll Runs
            </button>
          </div>
        </>
      )}
    </div>
  );
}
